import {POPUP} from "back/commons/widgets/popups";
import {IReg, IUiEnv, REG} from 'lib/commons/registry';
import {JSX} from "lib/commons/xml/dom";
import {ViewsContainer} from "lib/commons/views";
import "back/wsp/views/views_Perms";
import {InfoCurrentItem, InfoFocusNodeInItem} from "lib/wsp/item";
import {ItemViewerSingle} from "back/wsp/views/itemViewerSingle";
import {IScProtocolExecutor, JScProtocolParams, JScProtocolParamsOnUniverse, JScProtocolParamsSubActions, SCPRTC, ScUniverseProtocolExec} from "lib/core/scenariProtocol";
import {IWspUiEnv, JWspCreateParamsDB, JWspCreateParamsDB_DRF, JWspCreateParamsDB_DRV, JWspCreateParamsFS, JWspType, JWspTypeInst, Wsp, WSP} from "lib/wsp/wsp";
import {ESrcSt, srcRef} from "lib/wsp/src";
import {ERROR} from "lib/core/errorReport";
import {ImportScar} from "back/wsp/actions/srcActions";
import {ChainUniverse, IChainEnv} from "lib/wsp/chain";
import {AppFrameDeskFeat, JAppDef} from "back/core/appFrame";
import {IInfoBrokerPointer} from "lib/commons/infos";
import {BasicUniverse, IBasicUniversePointer, IUniverseEnv} from "lib/core/universe";
import {IPacksInUniverse, SKINPACK, WSPPACK} from "lib/wsp/pack";
import {ISecurityCtxPointer} from "lib/commons/security";


/** Paramètres typés du protocol Scenari (json parsé) liés à la désignation d'un wsp */
interface JScProtocolParamsOnWsp extends JScProtocolParams, JScProtocolParamsOnUniverse {
	wsp?: string
}

/**
 * ScWspProtocolExec spécialisé au contexte "Wsp"
 */
abstract class ScWspProtocolExec<P extends JScProtocolParamsOnWsp> extends ScUniverseProtocolExec<P, IChainEnv, ChainUniverse> implements IScProtocolExecutor<P> {
	private wsp: Wsp;

	/** Retourne un objet wsp **pas nécessairement chargé** */
	findWsp(params: P, reg: IReg<IWspUiEnv | IChainEnv>): Wsp {
		if (params.wsp) {
			if (!this.wsp) {
				const u = this.findUniverse(params, reg as IReg<IChainEnv>);
				if (!u || !u.wspServer) return;
				if ((reg as IReg<IWspUiEnv>).env.place) {
					this.wsp = (reg as IReg<IWspUiEnv>).env.place.getWsp(params.wsp);
					if (!this.wsp) (reg as IReg<IWspUiEnv>).env.place.registerWsp(new Wsp(u.wspServer, params.wsp));
				} else {
					this.wsp = new Wsp(u.wspServer, params.wsp);
				}
			}
			return this.wsp;
		} else
			return (reg as IReg<IWspUiEnv>).env.wsp;
	}
}


/**
 * Protocole scenari pour ouvrir une app (item, tâche...) en lien avec un wsp
 * Contexte minimum requis : IWspUiEnv, ou param "wsp"
 *
 * ex : scenari:{"action":"openAppOnWsp","appDefUniversIdParam":"u", "appDefWspCdParam":"wsp"}
 */
export interface JOpenAppOnWspScProtocolParams extends JScProtocolParamsOnWsp {

	action: "openAppOnWsp"

	/** params de l'app */
	appDef: JAppDef

	appDefUniversIdParam?: string;

	appDefWspCdParam?: string;

}

export class OpenAppOnWspScProtocolExec<P extends JOpenAppOnWspScProtocolParams> extends ScWspProtocolExec<P> {
	async execute(params: P, reg: IReg<IWspUiEnv | IChainEnv & IUiEnv | IUiEnv>, emitter: HTMLElement, ev: Event) {
		if (!params.appDef) params.appDef = {};

		/** Injection des paramètres contextuels */
		if (params.appDefUniversIdParam) {
			const universe = this.findUniverse(params, reg as IReg<IChainEnv>);
			if (universe)
				(params.appDef as any)[params.appDefUniversIdParam] = universe.getId();
		}
		if (params.appDefWspCdParam) {
			const wsp = this.findWsp(params, reg as IReg<IChainEnv>);
			if (wsp)
				(params.appDef as any)[params.appDefWspCdParam] = wsp.code;
		}

		return (desk as AppFrameDeskFeat).findAndOpenApp(params.appDef, ev);
	}
}

/**
 * Protocole scenari pour ouvrir une src (item, tâche...).
 * Contexte minimum requis : IWspUiEnv, ou param "wsp"
 *
 * ex : scenari:{"action":"openNode","refUri":"/my/item.xml"}
 */
export interface JOpenNodeScProtocolParams extends JScProtocolParamsOnWsp {

	action: "openNode"

	/** ref de la source à ouvrir */
	refUri: srcRef
}

export class OpenNodeScProtocolExec<P extends JOpenNodeScProtocolParams> extends ScWspProtocolExec<P> {
	async execute(params: P, reg: IReg<IWspUiEnv | IChainEnv & IUiEnv>, emitter: HTMLElement, ev: Event) {
		let handled = false;

		const wsp = this.findWsp(params, reg as IReg<IWspUiEnv | IChainEnv & IUiEnv>);
		if (!wsp) throw Error("No wsp context found");

		if ((reg.env as IInfoBrokerPointer).infoBroker) {
			const info = new InfoFocusNodeInItem('/', params.refUri);
			(reg.env as IInfoBrokerPointer).infoBroker.dispatchInfo(info, emitter);
			handled = info.handled;
		}
		if (!handled) {
			const itemViewer = new ItemViewerSingle();
			const root = <ViewsContainer î={{views: [itemViewer]}} style="flex:1;display:flex;min-height:0;min-width:0;flex-direction:column;"/> as ViewsContainer;
			await itemViewer.initViewer(wsp.reg, root, null, params.refUri);
			POPUP.showDialog(root, reg.env.uiRoot, {
				titleBar: {},
				initWidth: '90%',
				initHeight: '90%',
				resizer: {}
			});
		}
	}
}

/**
 * Protocole scenari pour ajouter une option dans un atelier existant donné
 * Contexte minimum requis : IWspUiEnv, ou param "wsp"
 *
 * ex : scenari:{
 *  "action":"addWspOptions",
 *  "universe":"chain",
 *  "wsp":"08GtVJ090s45AAPpI3D0Yd",
 *  "wspOptions":[
 *  	{
 *  		"key":"OpaleGenAuroraPrint3-8",
 *  		"title":"xxxxx"
 *  	}
 *  ]}
 */
export interface JAddWspOptionsScProtocolParams extends JScProtocolParamsOnWsp {

	action: "addWspOptions"

	wspOptions: JWspType[]
}

export class AddWspOptionsScProtocolExec<P extends JAddWspOptionsScProtocolParams> extends ScWspProtocolExec<P> {
	async execute(params: P, reg: IReg<IWspUiEnv | IChainEnv & IUiEnv>, emitter: HTMLElement, ev: Event) {
		let handled = false;

		const wsp = this.findWsp(params, reg as IReg<IWspUiEnv | IChainEnv & IUiEnv>);
		if (!wsp) throw Error("No wsp context found");

		if (!params?.wspOptions || !params.wspOptions.length) return false;

		let progress = POPUP.showProgress(emitter || reg.env.uiRoot, `Veuillez patienter...`);

		try {
			await wsp.waitForLoad();
			progress.close();

			// Confirmation
			const wspTitle = wsp.wspTitle;
			const optionsTitle = params.wspOptions.map((entry) => entry.title || entry.key).sort().join(", ");
			if (!await POPUP.confirm(params.wspOptions.length > 1 ?
				`Activer les options '${optionsTitle}' sur l'atelier '${wspTitle}' ?` :
				`Activer l'option '${optionsTitle}' sur l'atelier '${wspTitle}' ?`, emitter || reg.env.uiRoot)) return false;

			progress = POPUP.showProgress(emitter || reg.env.uiRoot, `Veuillez patienter...`);

			const newWspType: JWspTypeInst = new Object(wsp.wspType) as JWspTypeInst;
			if (!newWspType) throw Error("Current wsp not loadable");
			if (!newWspType.wspOptions) newWspType.wspOptions = [];
			let hasChanged = false;
			params.wspOptions.forEach((entry) => {
				if (!newWspType.wspOptions.find((opt) => (opt.key === entry.key && (!entry.version || opt.version.startsWith(entry.version))))) {
					hasChanged = true;
					newWspType.wspOptions.push(entry);
				}
			});
			if (hasChanged) await wsp.updateWspType(newWspType, true, true);
		} finally {
			progress.close();
		}
	}
}

/**
 * Protocole scenari pour importer une archive Scenari
 * Contexte minimum requis : IWspUiEnv, ou param "wsp"
 *
 * ex : scenari:{"action":"importScar","scarUrl":"https://xxx/yyy.scar","dstSpaceSrcUriFavour":"/test"}
 */
export interface JImportScarScProtocolParams extends JScProtocolParamsOnWsp {

	action: "importScar"

	/**  URL du scar */
	scarUrl: string

	/**
	 * Path préféré de l'espace de destination
	 * Si cet espace existe, alors un espace est créé en respectant la rêgle dstSpaceSrcUriFavour_{000}
	 */
	dstSpaceSrcUriFavour: string
}

export class ImportScarScProtocolExec<P extends JImportScarScProtocolParams> extends ScWspProtocolExec<P> {
	async execute(params: P, reg: IReg<IWspUiEnv | IChainEnv & IUiEnv>, emitter: HTMLElement, ev: Event) {
		const wsp = this.findWsp(params, reg as IReg<IWspUiEnv | IChainEnv & IUiEnv>);
		if (!wsp) throw Error("No wsp context found");
		if (!params.scarUrl) throw Error("Param 'scarUrl' required");
		if (!params.dstSpaceSrcUriFavour) throw Error("Param 'dstSpaceSrcUriFavour' required");

		// Confirmation : secu pour les actions en écriture
		const scarUrl = params.scarUrl;
		if (!await POPUP.confirm(`Importer l'archive provenant de '${scarUrl}' ?`, emitter || reg.env.uiRoot)) return false;

		// # Recherche d'un espace vide
		const maxChecks = 1000;
		let counter = 0;
		let dstSpaceSrcUriTarget;
		while (!dstSpaceSrcUriTarget && counter < maxChecks) {
			const dstSpaceSrcUriCandidate = params.dstSpaceSrcUriFavour + (counter ? "_" + counter : "");
			const node = await WSP.fetchSrcTree(wsp, null, dstSpaceSrcUriCandidate, 0, ["srcSt"]);
			if (node.srcSt != ESrcSt.none)
				counter++;
			else
				dstSpaceSrcUriTarget = dstSpaceSrcUriCandidate;
		}
		if (!dstSpaceSrcUriTarget) throw Error("No dest space available");

		// # Upload du scar
		const progress = POPUP.showProgress(emitter || reg.env.uiRoot, `Téléchargement en cours...`);
		let data: Blob;
		try {
			const resp = await fetch(params.scarUrl, {
				method: 'get',
				cache: "no-cache"
			});
			data = await resp.blob();
		} catch (e) {
			const scarUrl = params.scarUrl;
			await ERROR.show(`Impossible de télécharger l'archive Scenari: ${scarUrl}`, e);
			return false;
		} finally {
			progress.close();
		}

		if (data && data.type === "application/scenari.scar") {
			const leafname = params.scarUrl.split('/').pop();
			const file = new File([data], leafname);
			if (await ImportScar.doImportScar(wsp, emitter || reg.env.uiRoot, dstSpaceSrcUriTarget, file, false)) {
				//remontée erreur au user déjà assumée dans ImportScar.doImportScar().
				if ((reg.env as IInfoBrokerPointer).infoBroker) {
					// focus sur l'espace
					const info = new InfoCurrentItem(dstSpaceSrcUriTarget);
					(reg.env as IInfoBrokerPointer).infoBroker.dispatchInfo(info, emitter);
				}
			}
		} else {
			const scarUrl = params.scarUrl;
			const dataType = data.type;
			await ERROR.show(`La ressource pointée '${scarUrl}' n'est pas une archive Scenari (type '${dataType}' au lieu de 'application/scenari.scar')`);
			return false;
		}
	}
}


/**
 * Protocole scenari pour créer un atelier
 * Contexte requis : IUiEnv avec le param univers, ou IChainEnv
 * Contexte des subActions : IWspUiEnv (avec l'atelier créé)
 *
 * ex : scenari:{"action":"createWsp","wspParams":{...}}
 */
export interface JCreateWspScProtocolParams extends JScProtocolParamsOnUniverse, JScProtocolParamsSubActions {

	action: "createWsp"

	wspParams: JWspCreateParamsFS | JWspCreateParamsDB | JWspCreateParamsDB_DRF | JWspCreateParamsDB_DRV

	wspType: JWspTypeInst

}

export class CreateWspScProtocolExec<P extends JCreateWspScProtocolParams> extends ScUniverseProtocolExec<P, IChainEnv, ChainUniverse> {
	async execute(params: P, reg: IReg<IChainEnv & IUiEnv | IUniverseEnv & IUiEnv>, emitter: HTMLElement, ev: Event) {
		const universe = this.findUniverse(params, reg as IReg<IChainEnv>);
		let wspLabel: string;
		try {
			if (!universe || !universe.wspServer)
				throw Error("chain univers required (param 'univers')");

			if (!params.wspParams) throw Error("Param 'wspParams' required");
			if (!params.wspType) throw Error("Param 'wspType' required");

			const wspCd = (params.wspParams as JWspCreateParamsDB).alias || (params.wspParams as JWspCreateParamsFS).code;
			let wsp: Wsp;
			if (wspCd) {
				// Contrôle d'existance du wsp
				wsp = new Wsp(universe.wspServer, (params.wspParams as JWspCreateParamsDB).alias || (params.wspParams as JWspCreateParamsFS).code);
				await wsp.waitForLoad();
				if (wsp.infoWspError && wsp.infoWspError.status === "noWsp") wsp = null;
			}
			if (!wsp) {
				wspLabel = params.wspParams.title || (params.wspParams as JWspCreateParamsDB).alias || (params.wspParams as JWspCreateParamsFS).code;
				// Confirmation : secu pour les actions en écriture
				if (!await POPUP.confirm(`Créer l'atelier '${wspLabel}' ?`, emitter || reg.env.uiRoot)) return false;
				wsp = await WSP.createWsp(universe.wspServer, params.wspParams, params.wspType, true);
			}

			if (params.subActions) {
				const newReg = REG.createSubReg(reg, wsp.reg);
				params.subActions.forEach((act) => {
					const action = SCPRTC.findAction(act.action, reg);
					if (action) {
						action.execute(act, newReg, emitter, ev);
					} else {
						console.log(`No action registered for scenari: protocol '${act.action}'`);
					}
				});

			}

		} catch (e) {
			await ERROR.show(wspLabel ? `Création de l'atelier '${wspLabel}' en échec.` : 'Création de l\'atelier en échec.', e, params);
			return false;
		}
	}
}

/**
 * Protocole scenari pour installer un wspPack
 * Contexte requis : IUiEnv avec le param univers, ou IPacksInUniverse
 *
 * ex : scenari:{
 * 				"action":"installPack",
 * 			  "universe":"chain",
 * 				"packUrl":"http://xxx/yyy.wsppack"
 * 		}
 */
export interface JInstallPackScProtocolParams extends JScProtocolParamsOnUniverse {

	action: "installPack"

	/**  URL du pack */
	packUrl: string

	/** Nom de du skin à installer (utilisé pour construire les retours UI) */
	packName?: string
}

export class InstallPackScProtocolExec<P extends JInstallPackScProtocolParams> extends ScUniverseProtocolExec<P, IBasicUniversePointer & IPacksInUniverse & ISecurityCtxPointer, BasicUniverse> {

	async execute(params: P, reg: IReg<IUniverseEnv & IUiEnv>, emitter: HTMLElement, ev: Event) {
		const universe = this.findUniverse(params, reg as IReg<IBasicUniversePointer>);
		if (!(universe as IPacksInUniverse)?.packServer)
			throw Error("'univers' required (param 'univers')");
		if (!params.packUrl) throw Error("Param 'packUrl' required");
		const packUrl = params.packUrl;
		const packName = params.packName || params.packUrl.split('/').pop();

		// Verif perm
		if (!universe.reg.hasPerm("install.wspPack"))
			if (!await POPUP.confirm(`Vous ne disposez pas des habilitations requises`, emitter || reg.env.uiRoot)) return false;

		// Confirmation : secu pour les actions en écriture
		if (!await POPUP.confirm(`Installer le pack '${packName}' provenant de '${packUrl}' ?`, emitter || reg.env.uiRoot)) return false;

		// # Download du pack
		let data: Blob;
		const progress = POPUP.showProgress(emitter || reg.env.uiRoot, `Téléchargement du pack '${packName}' en cours...`);
		try {
			const resp = await fetch(params.packUrl, {
				method: 'get',
				cache: 'no-cache'
			});
			data = await resp.blob();
			progress.close();
		} catch (e) {
			progress.close();
			await ERROR.show(`Impossible de télécharger le pack '${packName}' : ${packUrl}`, e);
			return false;
		}

		if (data && data.type === 'application/scenari.wsppack') {
			//application/scenari.skinpack
			const leafname = params.packUrl.split('/').pop();
			const file = new File([data], leafname);
			const progress = POPUP.showProgress(emitter || reg.env.uiRoot, `Installation du pack '${packName}' en cours...`);
			try {
				const pack = await WSPPACK.installPack((universe as IPacksInUniverse).packServer, file, true);
				progress.close();
				if (pack?.error) {
					const error = WSPPACK.errorToString(pack.error);
					await ERROR.show(`Installation du pack '${packName}' en erreur : ${error}`);
					return false;
				}
			} catch (e) {
				progress.close();
			}

		} else {
			const mimeType = data.type;
			await ERROR.show(`La ressource '${packUrl}' n'est pas un wspPack Scenari (type '${mimeType}' au lieu de 'application/scenari.wsppack')`);
			return false;
		}
	}
}

/**
 * Protocole scenari pour installer un wspPack
 * Contexte requis : IUiEnv avec le param univers, ou IPacksInUniverse
 *
 * ex : scenari:{
 * 				"action":"installSkin",
 * 			  "universe":"chain",
 * 				"packUrl":"http://xxx/yyy.skinpack"
 * 		}
 */

export interface JInstalllSkinScProtocolParams extends JScProtocolParamsOnUniverse {

	action: "installSkin"

	/**  URL du pack */
	packUrl: string

	/** Nom de du skin à installer (utilisé pour construire les retours UI) */
	packName?: string

}

export class InstallSkinScProtocolExec<P extends JInstalllSkinScProtocolParams> extends ScUniverseProtocolExec<P, IBasicUniversePointer & IPacksInUniverse & ISecurityCtxPointer, BasicUniverse> {

	async execute(params: P, reg: IReg<IUniverseEnv & IUiEnv>, emitter: HTMLElement, ev: Event) {
		const universe = this.findUniverse(params, reg as IReg<IBasicUniversePointer>);
		if (!(universe as IPacksInUniverse)?.packServer)
			throw Error("'universe'' required (param 'univers')");
		if (!params.packUrl) throw Error("Param 'packUrl' required");
		const packUrl = params.packUrl;
		const packName = params.packName || params.packUrl.split('/').pop();

		// Verif perm
		if (!universe.reg.hasPerm("install.skinPack"))
			if (!await POPUP.confirm(`Vous ne disposez pas des habilitations requises`, emitter || reg.env.uiRoot)) return false;

		// Confirmation : secu pour les actions en écriture
		if (!await POPUP.confirm(`Installer l'habillage graphique '${packName}' provenant de '${packUrl}' ?`, emitter || reg.env.uiRoot)) return false;

		// # Download du pack
		let data: Blob;
		const progress = POPUP.showProgress(emitter || reg.env.uiRoot, `Téléchargement de l'habillage graphique '${packName}' en cours...`);
		try {
			const resp = await fetch(params.packUrl, {
				method: 'get',
				cache: 'no-cache'
			});
			data = await resp.blob();
			progress.close();
		} catch (e) {
			progress.close();
			await ERROR.show(`Impossible de télécharger l'habillage graphique '${packName}'`, e);
			return false;
		}

		if (data && data.type === 'application/scenari.skinpack') {
			const leafname = params.packUrl.split('/').pop();
			const file = new File([data], leafname);
			const progress = POPUP.showProgress(emitter || reg.env.uiRoot, `Installation de l'habillage graphique '${packName}' en cours..."`);
			try {
				const pack = await SKINPACK.install((universe as IPacksInUniverse).packServer, file);
				progress.close();
				if (!SKINPACK.isStatusInstalled(pack?.installStatus)) {
					await ERROR.show(`L'installation de l'habillage graphique '${packName}' a échoué`);
					return false;
				}
			} catch (e) {
				progress.close();
			}

		} else {
			const mimeType = data.type;
			await ERROR.show(`La ressource pointée '${packUrl}' n'est pas un habillage graphique Scenari (type '${mimeType}' au lieu de 'application/scenari.skinpack')`);
			return false;
		}
	}
}

/**
 * Ajoute les différents exécutors WSp au registre
 * @param reg
 */
export function registerWspProtocolsActions(reg: IReg<any>) {
	reg.addToList(SCPRTC.LIST_EXECUTORS, 'openNode', 1, new OpenNodeScProtocolExec());
	reg.addToList(SCPRTC.LIST_EXECUTORS, 'importScar', 1, new ImportScarScProtocolExec());
	reg.addToList(SCPRTC.LIST_EXECUTORS, 'createWsp', 1, new CreateWspScProtocolExec());
	reg.addToList(SCPRTC.LIST_EXECUTORS, 'addWspOptions', 1, new AddWspOptionsScProtocolExec());
	reg.addToList(SCPRTC.LIST_EXECUTORS, 'installPack', 1, new InstallPackScProtocolExec());
	reg.addToList(SCPRTC.LIST_EXECUTORS, 'installSkin', 1, new InstallSkinScProtocolExec());
	reg.addToList(SCPRTC.LIST_EXECUTORS, 'openAppOnWsp', 1, new OpenAppOnWspScProtocolExec());
}